/** Structured error surfaces for the onboarding build flow. */

import _t from '@/i18n/t';
import { getAiLimitMessage } from '@/shared/utils/apiError';

export interface StreamError {
  source: 'stream';
  /** Lane key (mapped from backend agentType), if known. */
  agentKey?: string;
  /** Raw backend agentType (e.g. 'site-page-fetcher'). */
  agentType?: string;
  /** Pipeline phase (e.g. 'collect', 'synthesize'), if backend reports it. */
  phase?: string;
  /** Human-readable message. */
  message: string;
  /** Whether a retry has a reasonable chance of succeeding. */
  retryable: boolean;
}

export interface BootstrapError {
  source: 'bootstrap';
  /** Which entity step failed (persona, kb, content, ...). */
  step?: string;
  message: string;
  retryable: boolean;
  /** Per-entity detail dict from the API (errors: { persona: '...', kb: '...' }). */
  details?: Record<string, string>;
}

interface RawStreamErrorPayload {
  agentKey?: string;
  agentType?: string;
  phase?: string;
  message?: string;
  retryable?: boolean;
}

export function toStreamError(raw: unknown): StreamError {
  if (typeof raw === 'string') {
    return { source: 'stream', message: raw, retryable: true };
  }
  if (raw && typeof raw === 'object') {
    const r = raw as RawStreamErrorPayload;
    return {
      source: 'stream',
      agentKey: r.agentKey,
      agentType: r.agentType,
      phase: r.phase,
      message: r.message || 'Scan failed',
      retryable: r.retryable ?? true,
    };
  }
  return { source: 'stream', message: 'Scan failed', retryable: true };
}

interface ScanCreateErrorBody {
  error?: string;
  companyId?: number;
  companyName?: string;
}

export function parseScanCreateError(status: number, body: unknown): StreamError {
  if (status === 409) {
    const b = (body ?? {}) as ScanCreateErrorBody;
    return {
      source: 'stream',
      message: _t('onboarding.scan.duplicateUrl', { company: b.companyName ?? '' }),
      retryable: false,
    };
  }
  const limitMessage = status === 402 ? getAiLimitMessage(body) : null;
  if (limitMessage) return { source: 'stream', message: limitMessage, retryable: false };
  return { source: 'stream', message: _t('onboarding.scan.genericError', { status }), retryable: true };
}
